/**
 * useRoomCleanMutation — senyals de la card (`plans/calendar-card-signals.md` §6 B1.3 i F2.1).
 *
 * «Habitació {nom} neta» / «Desfer»: `POST` i `DELETE /physical-rooms/:id/clean`. El marc del llum
 * de dalt de qui arriba avui passa a verd (neta) o taronja (s'ha de netejar).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/services/api';

export interface RoomCleanVariables {
  physicalRoomId: string;
  /** `true` = marcar neta (`POST`); `false` = desfer (`DELETE`). */
  clean: boolean;
  /** La reserva des de la qual es marca: se'n refresca la fitxa completa. */
  reservationId?: string;
}

/** La crida en si, fora del hook perquè es pugui provar amb `api` mockejat. */
export async function sendRoomClean({ physicalRoomId, clean }: RoomCleanVariables) {
  const url = `/physical-rooms/${encodeURIComponent(physicalRoomId)}/clean`;
  const response = clean ? await api.post(url) : await api.delete(url);
  return response.data;
}

export function useRoomCleanMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendRoomClean,
    onSuccess: (_data, { reservationId }) => {
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'], exact: false });
      // «Posar al dia»: el comptador del botó del calendari surt d'aquesta consulta.
      queryClient.invalidateQueries({ queryKey: ['stay-catch-up'] });
      if (reservationId) {
        queryClient.invalidateQueries({ queryKey: ['reservation-full', reservationId] });
      }
    },
    onError: (error: unknown) => {
      const detail = error instanceof Error && error.message ? `: ${error.message}` : '';
      toast.error(`No s'ha pogut desar la neteja de l'habitació${detail}`);
    },
  });
}

export default useRoomCleanMutation;
